"use client";
import {
  getLanguageFromPathname,
  withLanguagePrefix,
} from "@/app/lib/i18n-routing";
import { motion } from "framer-motion";
import { usePathname } from "next/navigation";
import ButtonGroup from "../ui/ButtonGroup";
import { withMediaUrl } from "@/app/lib/basePath";
import Image from "next/image";
import { useState } from "react";
import ProductMedia from "./ProductMedia";

const ProductsSideBySide = ({ data }: any) => {
  if (!data) return null;
  const products = data?.items || [];
  const pathname = usePathname();
  const currentLanguage = getLanguageFromPathname(pathname);
  const localizedPath = (path: string) =>
    withLanguagePrefix(path, currentLanguage);
  const [hoverStates, setHoverStates] = useState<boolean[]>(Array(products.length).fill(false));

  return (
    <div className="relative text-[#e8eaf0] py-6 lg:py-8">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.6 }}
        className="max-w-4xl mx-auto px-3 text-center"
      >
        {/* <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-indigo-400/30 bg-indigo-500/5 text-[11px] uppercase tracking-wider text-indigo-300 mb-6">
          <span className="w-1.5 h-1.5 rounded-full bg-indigo-400" />
          {data?.text || 'Our Products'}
        </div> */}
        <h2 className="section-title !mb-2">
          {data?.title}{" "}
          <span className="section-highlight-title">
            {data?.titleHighlight}
          </span>
        </h2>
        <p className="section-description mx-auto">
          {data?.subtitle}
        </p>
      </motion.div>

      <div className="flex flex-col gap-4 lg:gap-12 mt-5 lg:mt-12">
      {products?.map((f: any, i: any) => {
        const isHovered = hoverStates[i] || false;
        const isRight = i % 2 === 0; // Even indices start from right, odd from left

        return (
          <div className="relative" key={i}>
            <div
              className="absolute top-1/2 right-[10%] -translate-y-1/2 w-1/2 lg:w-[500px] h-[500px] rounded-full pointer-events-none blur-3xl"
              style={{ background: "rgba(99,102,241,0.18)" }}
            />
            <div
              className="absolute top-1/2 left-[10%] -translate-y-1/2 w-1/2 lg:w-[500px] h-[500px] rounded-full pointer-events-none blur-3xl"
              style={{ background: "rgba(99,102,241,0.15)" }}
            />
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7 }}
              className="relative grid lg:grid-cols-2 gap-4 lg:gap-10 items-center"
            >
              <div className={`order-2 lg:order-${isRight ? '1' : '2'}`}>
                <h2 className="text-[20px] lg:text-[22px] xl:text-[30px] font-bold lg:mb-3">
                  {f?.title}
                </h2>
                <p className="text-xl font-semibold mb-4 section-highlight-title">
                  {f?.text}
                </p>
                <div
                    className="text-white/70 text-sm lg:text-base leading-[1.7] mb-8 post-content !mx-0"
                    dangerouslySetInnerHTML={{ __html: f.descriptionHtml }}
                  ></div>
                {/* <p className="">
                  {f?.descriptionHtml}
                </p> */}

                <ButtonGroup
                  buttons={f?.buttons}
                  localizedPath={localizedPath}
                  containerClass="flex flex-wrap items-center gap-3 justify-center lg:justify-start"
                />
              </div>
              <div className={`relative order-1 lg:order-${isRight ? '2' : '1'}`}>
                <div
                  onMouseEnter={() => {
                    const newHoverStates = [...hoverStates];
                    newHoverStates[i] = true;
                    setHoverStates(newHoverStates);
                  }}
                  onMouseLeave={() => {
                    const newHoverStates = [...hoverStates];
                    newHoverStates[i] = false;
                    setHoverStates(newHoverStates);
                  }}
                  className="rounded-[14px] border border-white/10 bg-[#0a1018] overflow-hidden shadow-[0_30px_80px_-20px_rgba(99,102,241,0.25)] transition-transform duration-[400ms] ease-out"
                  style={{
                    transform: isHovered
                      ? "perspective(800px) rotateY(0deg) rotateX(0deg)"
                      : isRight 
                        ? "perspective(800px) rotateY(-4deg) rotateX(2deg)"
                        : "perspective(800px) rotateY(4deg) rotateX(2deg)",
                  }}
                >
                  <div className="aspect-video relative">
                    <ProductMedia media={f?.media || []} />
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        );
      })}
      </div>
    </div>
  );
};

export default ProductsSideBySide;
